import { withLayout } from '../../domain/active-layout.js';
import { MONEY_LIMIT } from '../../domain/economy.js';
import { AIRPORT_MINUTE } from '../../domain/timetable.js';
import { validateTerminalRecord, restingBody, type ManagedPolicy, type PassengerRecord, type TerminalRecord } from '../../domain/terminal-record.js';
import { PASSENGER_WALK_SPEED, RACK_PICKUP } from '../../domain/config.js';
import { capacity } from '../../domain/economy.js';
import { gateWalk, arrivalStep, waitingSeats } from '../../domain/gateflow.js';
import { inquiryPoint, queuePoint, servicePoint } from '../../domain/queues.js';
import { tier } from '../../domain/airports.js';
import { TIMETABLE_KEYS } from '../../domain/timetable.js';
import { airportRun } from '../../domain/terminal-record.js';
import type { FlowPassenger } from './types.js';
import { flowFactsFromRecord, flowStateFromRecord } from './facts.js';
import { runFlow, settleFlowCash } from './model.js';

/**
 * A managed airport keeps its own L1 clock, anchored on a frame aligned to the 10-airport-minute departure grid.
 * It never reads the live world frame, so rebuilding or reloading another airport cannot shift its schedule phase.
 */
export const MANAGED_EPOCH = 3600 * 10 * AIRPORT_MINUTE;
export interface ManagedContext { prestige: number }
/** Later-act offline policy: the same L1 model at half income, for at most eight hours of absence. */
export const OFFLINE_CAP_SECONDS = 8 * 3600;
export type ManagedResult = { ok: true; record: TerminalRecord } | { ok: false; error: string };

/**
 * Hand an airport's live inventory to L1. Travellers, bags and wrappers move into L1 pools with conservation.
 * Temporary captain boosts end, because no captain is present. Returns errors as values; the input is never changed.
 */
export function startManaging(source: TerminalRecord, policy: ManagedPolicy, context: ManagedContext & { randomState: number }): ManagedResult {
    return withLayout(source.airportId, () => startManagingOn(source, policy, context));
}
function startManagingOn(source: TerminalRecord, policy: ManagedPolicy, context: ManagedContext & { randomState: number }): ManagedResult {
    if (source.managed) return { ok: false, error: 'This airport is already managed.' };
    if (!['active', 'half'].includes(policy) || !Number.isSafeInteger(context.randomState) || context.randomState < 0) return { ok: false, error: 'Managed airport policy or seed is invalid.' };
    const record = structuredClone(source);
    record.economy.incomeUntil = 0; record.economy.expressUntil = 0;
    const facts = flowFactsFromRecord(record, { frame: MANAGED_EPOCH, prestige: context.prestige, randomState: context.randomState >>> 0 });
    const projected = flowStateFromRecord(record, facts);
    if (!projected.ok) return projected;
    Object.assign(record, { passengers: [], bags: [], litter: [], managed: { policy, flow: projected.state } });
    return checked(record);
}

/**
 * Run a managed airport for whole seconds under its policy, then settle floor cash into its desk and machine piles.
 * Floor cash waits uncapped for a visit, up to the integer-safe money limit; anything above it is recorded as blocked.
 * Unmanaged (frozen) airports are returned unchanged.
 */
export function advanceManaged(source: TerminalRecord, seconds: number, context: ManagedContext, policy?: ManagedPolicy): ManagedResult {
    return withLayout(source.airportId, () => advanceManagedOn(source, seconds, context, policy));
}
function advanceManagedOn(source: TerminalRecord, seconds: number, context: ManagedContext, policy?: ManagedPolicy): ManagedResult {
    if (!Number.isSafeInteger(seconds) || seconds < 0) return { ok: false, error: 'Managed time must be whole nonnegative seconds.' };
    if (!source.managed) return { ok: true, record: source };
    const record = structuredClone(source), managed = record.managed!;
    const facts = flowFactsFromRecord(record, { frame: managed.flow.frame, prestige: context.prestige, randomState: managed.flow.randomState });
    const before = { served: managed.flow.departments.map(d => d.servedMilli), sold: managed.flow.vending.map(v => v.soldMilli), flights: managed.flow.flights };
    const ran = runFlow(managed.flow, facts, seconds, policy ?? managed.policy);
    if (!ran.ok) return ran;
    let flow = ran.state;
    const settled = settleFlowCash(flow);
    flow = settled.state;
    const credit = (account: { pending: number }, amount: number) => {
        const added = Math.min(amount, MONEY_LIMIT - account.pending);
        account.pending += added; flow.blockedRevenue += amount - added;
        record.economy.earned = Math.min(MONEY_LIMIT, record.economy.earned + added);
    };
    for (const { index, amount } of settled.departments) credit(record.stations[index], amount);
    for (const { index, amount } of settled.vending) credit(record.vending[index], amount);
    flow.departments.forEach((d, n) => { record.stations[d.index].served += Math.floor(d.servedMilli / 1000) - Math.floor(before.served[n] / 1000); });
    flow.vending.forEach((v, n) => { record.vending[v.index].sold += Math.floor(v.soldMilli / 1000) - Math.floor(before.sold[n] / 1000); });
    record.economy.flights += flow.flights - before.flights;
    Object.assign(record.counters, { serial: flow.serial, spawnClock: flow.spawnClock, nextArrivalIn: flow.nextArrivalIn, dissatisfied: flow.dissatisfied });
    managed.flow = flow;
    return checked(record);
}

function checked(record: TerminalRecord): ManagedResult {
    const result = validateTerminalRecord(record, { people: new Set() });
    return result.ok ? { ok: true, record: result.record } : result;
}

/**
 * Return a managed airport to live play. Every L1 traveller, bag and wrapper becomes a resting body at its authored
 * queue, desk, lounge, question, gate or deplaning spot; serials, VIP flags and patience carry over unchanged, and
 * floor cash settles first so nothing is earned twice. In-progress walks restart from those spots (an approximation).
 */
export function stopManaging(source: TerminalRecord, context: ManagedContext): ManagedResult {
    return withLayout(source.airportId, () => stopManagingOn(source, context));
}
function stopManagingOn(source: TerminalRecord, context: ManagedContext): ManagedResult {
    if (!source.managed) return { ok: false, error: 'This airport is not managed.' };
    const settled = advanceManaged(source, 0, context);
    if (!settled.ok) return settled;
    const record = settled.record, flow = record.managed!.flow, frame = flow.frame, port = tier({ run: airportRun(record.airportId) });
    const passengers: PassengerRecord[] = [];
    const add = (p: FlowPassenger, stage: number, state: number, at: { x: number; y: number }, extra: Partial<PassengerRecord> = {}) => passengers.push({
        serial: p.serial, stage, state, slot: -1, remaining: 0, queuedAge: 0, tint: p.serial % 6, vip: p.vip, paid: false, vending: -1, shopFrames: 0, gate: -1,
        seat: -1, seated: false, walkStep: 0, releaseIn: 0, inquired: !!p.inquired, arriving: false, entering: false, snack: false,
        happiness: p.happiness ?? 80, patience: p.patienceFrames, leaving: p.leavingFrames, sad: false, body: restingBody(at.x, at.y, PASSENGER_WALK_SPEED), ...extra });
    for (const d of flow.departments) {
        d.queued.forEach((p, rank) => add(p, d.index, 0, queuePoint(d.index, rank)));
        for (const p of [...d.inService, ...d.completed]) {
            const slot = p.slot ?? 0, at = servicePoint(d.index, slot, capacity(record.stations[d.index]));
            if ((p.approachFrames ?? 0) > 0) add(p, d.index, 1, at, { slot });
            else add(p, d.index, 2, at, { slot, remaining: (p.remainingWork ?? 0) / 4000 });
        }
    }
    const behind = new Map<number, number>();
    for (const p of flow.transit) {
        const rank = (flow.departments.find(d => d.index === p.destination)?.queued.length ?? 0) + (behind.get(p.destination) ?? 0);
        behind.set(p.destination, (behind.get(p.destination) ?? 0) + 1);
        add(p, p.destination, 0, queuePoint(p.destination, Math.min(rank, 11)));
    }
    const seats = waitingSeats(record.economy.chairLevel, port.maxX);
    flow.lounge.forEach((p, n) => add(p, 6, 6, seats[n] ?? queuePoint(6, 0)));
    for (const p of flow.inquiry) add(p, 6, 7, inquiryPoint(), { remaining: p.answerFrames, inquired: true });
    for (const r of flow.reservations) add({ serial: r.serial, vip: r.vip, massMilli: 1000, patienceFrames: 0, leavingFrames: 0 }, 6, 3, servicePoint(6, 0), { gate: r.gate });
    for (const q of flow.inbound) {
        // Still deplaning: back at the aircraft door; otherwise inside the gate, heading for the exit.
        const walk = gateWalk(record.airportId, q.gate, true), deplaning = q.gateFrames > 0;
        add({ serial: q.serial, vip: q.serial % 9 === 0, massMilli: 1000, patienceFrames: 0, leavingFrames: 0 }, -1, 0, deplaning ? walk[walk.length - 1] : walk[0], { arriving: true, gate: q.gate, walkStep: deplaning ? arrivalStep(walk) : 2, remaining: q.remainingFrames });
    }
    const porters = record.staff.filter(s => s.role === 'porter');
    const bags: TerminalRecord['bags'] = [];
    flow.porters.forEach((p, n) => { for (let k = 0; k < p.carriedMilli / 1000 && porters[n]; k++) bags.push({ serial: bags.length + 1, carrier: { kind: 'staff', id: porters[n].id }, x: porters[n].body.at.x, y: porters[n].body.at.y }); });
    for (let k = 0; k < flow.freeBagMilli / 1000; k++) bags.push({ serial: bags.length + 1, carrier: null, x: RACK_PICKUP.x - 18 + (k % 3) * 18, y: RACK_PICKUP.y + Math.floor(k / 3) * 12 });
    const litter = Array.from({ length: flow.litter }, (_, k) => { const at = queuePoint(2, k % 12); return { serial: k + 1, kind: k % 3, carrier: null, x: at.x + (k % 3 - 1) * 30, y: at.y + 40 + (k % 4) * 14 }; });
    record.operations.planes = flow.planes.map(p => ({ ...p, nextDepartureFrame: p.nextDepartureFrame - frame,
        ...Object.fromEntries(TIMETABLE_KEYS.map(key => [key, p[key] < 0 ? null : p[key] - frame])) })) as TerminalRecord['operations']['planes'];
    // Travellers walking to a gate already hold seats: the plane's boarded count in L1 excludes them, as in live play.
    const { managed: _managed, ...live } = record;
    return checked({ ...live, passengers, bags, litter });
}

/** Catch up a managed airport after wall-clock absence: half policy, capped at eight hours, remainders carried. Frozen airports stay frozen. */
export function catchUpManaged(source: TerminalRecord, elapsedMs: number, context: ManagedContext): ManagedResult {
    return withLayout(source.airportId, () => catchUpManagedOn(source, elapsedMs, context));
}
function catchUpManagedOn(source: TerminalRecord, elapsedMs: number, context: ManagedContext): ManagedResult {
    const seconds = Number.isFinite(elapsedMs) && elapsedMs > 0 ? Math.min(OFFLINE_CAP_SECONDS, Math.floor(elapsedMs / 1000)) : 0;
    return advanceManaged(source, seconds, context, 'half');
}
